import { useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";

import { CurrencyEnum } from "../../../../../enums/currencies.enum";
import { getGoHireRoute, getJobDetailsRoute } from "../../../../../routers/routeUtils";

import { ApplyCriteriaEnum } from "../../../enum/jobEnums";
import JobBoostStepLooked from "../../../components/JobBoost-Looked";
import { CreateJobHeader, CreateJobFooter } from "../../../components";
import { createTalentJobSchema } from "../../../schemas/createJobSchema";
import HiringJourneyStepLooked from "../../../components/HiringJourney-Looked";
import { useCreateTalentJob } from "../../../api/mutations/useCreateTalentJob";
import { CreateTalentJob as CreateTalentJobType } from "../../../types/createJobSchema";

import JobPublished from "./Steps/JobPublished";
import JobDetailsStep from "./Steps/JobDetails";
import PreviewAndPublishJob from "./Steps/PreviewAndPublishJob";
import { CancelButtonIcon, PrevButtonIcon } from "../../../assets/icons/go-hire-page-icons";

const CreateTalentJob = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [jobId, setJobId] = useState<number | null>(null);

  const methods = useForm<CreateTalentJobType>({
    resolver: zodResolver(createTalentJobSchema),
    mode: "all",
    defaultValues: {
      jobLocation: [],
      nationality: [],
      talentProfessionalLocation: [],
      salaryCurrency: CurrencyEnum.EGP,
      languagesSpoken: [],
      isExternal: false,
    },
  });

  const handleGoBack = () => {
    if (step === 0 || step === 4) {
      setStep(0);
      navigate(getGoHireRoute());
    } else {
      setStep((prev) => prev - 1);
    }
  };

  const { t } = useTranslation("goHire", { keyPrefix: "createJobPage" });

  const handleValidateJobDetails = async () => {
    let isValid = true;
    isValid = await methods.trigger([
      "jobName",
      "primaryPosition",
      "secondaryPosition",
      "jobMode",
      "jobLocation",
      "jobDescription",
      "applyEnds",
      "salaryAmount",
      "salaryCurrency",
      "salaryPer",
      "howManyHiresNeeded",
      "nationality",
      "talentProfessionalLocation",
      ApplyCriteriaEnum.onlyVerifiedFootballers,
      ApplyCriteriaEnum.onlyWithTestimonials,
      ApplyCriteriaEnum.profileCompletionAbove80,
      "languagesSpoken",
      "majorInjury",
      "willingnessToRelocate",
      "introductoryVideo",
      "clubsWorkedIn",
    ]);
    return isValid;
  };

  const { mutate: createJobTalent, isPending } = useCreateTalentJob({
    onSuccess: (data) => {
      setStep(4);
      setJobId(data.id);
    },
  });

  const handleCreateJobTalent = (data: CreateTalentJobType) => {
    createJobTalent(data);
  };

  const handleGoToNextStep = async () => {
    const isValid = await handleValidateJobDetails();
    if (!isValid) return;

    if (step === 3) {
      const isFormValid = await methods.trigger();
      if (!isFormValid) return;
      methods.handleSubmit(handleCreateJobTalent)();
      return;
    }

    if (step === 4) return;
    setStep((prevStep) => prevStep + 1);
  };

  const handlePreviewClick = async () => {
    const isValid = await handleValidateJobDetails();
    if (!isValid) return;
    setStep(3);
  };

  const jobDetails = methods.getValues();
  const createJobStepsMapper: Record<number, JSX.Element> = {
    0: <JobDetailsStep />,
    1: <HiringJourneyStepLooked />,
    2: <JobBoostStepLooked />,
    3: <PreviewAndPublishJob jobDetails={jobDetails} />,
    4: <JobPublished />,
  };

  const BackButtonIcon = step !== 0 ? <PrevButtonIcon /> : <CancelButtonIcon />;

  return (
    <FormProvider {...methods}>
      <Box sx={styles.container}>
        <CreateJobHeader handleGoBack={handleGoBack} />
        {createJobStepsMapper[step]}
        {step !== 4 && (
          <CreateJobFooter
            handleGoBack={handleGoBack}
            BackButtonIcon={BackButtonIcon}
            backButtonLabel={step !== 0 ? t("back") : t("cancel")}
            handleGoToNextStep={handleGoToNextStep}
            handlePreviewClick={handlePreviewClick}
            nextButtonLabel={step === 3 ? t("publish") : t("next")}
            isSubmitting={isPending}
            jobId={jobId}
            showPreviewButton={step !== 3}
          />
        )}
      </Box>
    </FormProvider>
  );
};

export default CreateTalentJob;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    background: "#FFF",
    minHeight: "100vh",
  },
};
